import React from "react";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { SurveyDefinition } from "./survey";
import { PrivacyCenterDto } from "../../common/service/server/dto/PrivacyCenterDto";
import { PrivacyCenterSettingsProps } from "../components/superorg/settings/PrivacyCenterSettings";

export const EEA_UK_SURVEY_NAME = "eea-uk-survey";

export const PERSONAL_DATA_LARGE_SCALE_QUESTION_SLUG = "personal-data-large-scale";
export const largeScaleQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: PERSONAL_DATA_LARGE_SCALE_QUESTION_SLUG,
    question: "Does your business process personal data of EEA/UK/CH residents on a “large scale”?",
    helpText: (
      <>
        <p>Large scale processing is not common. Here are some examples where it would apply:</p>
        <ul className="list-disc list-inside">
          <li>
            Your business processes unaggregated personal data collected from monitoring a publicly
            accessible area, such as a park, airport or train station, on a large scale
          </li>
          <li>
            Your business routinely processes real-time geolocation data of customers across
            multiple EU countries for statistical purposes
          </li>
          <li>
            Your business is an insurance company, bank, search engine or internet service provider
          </li>
          <li>
            Your business processes the personal data of a significant portion of the population of
            the UK or any EU country (‘processing’ includes tracking online behavior)
          </li>
        </ul>
        <p style={{ marginTop: "6px" }}>
          Click{" "}
          <a
            href="https://knowledge.truevault.com/article/226-large-scale-processing"
            className="text-blue-600 underline"
            target="_blank"
            rel="norefferer noopener noreferrer"
          >
            here
          </a>{" "}
          to read more about “large scale” data processing.
        </p>
      </>
    ),
  };
};

export const EEA_PHYSICAL_PRESENCE_SLUG = "eea-physical-presence";
export const eeaPhysicalPresenceQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: EEA_PHYSICAL_PRESENCE_SLUG,
    question: "Does your business have a physical presence in the EEA, UK, or Switzerland?",
    helpText: (
      <>
        <p>
          A physical presence would include, for example, a retail location, a corporate office, or
          any official address where your business can receive correspondence and notices.
        </p>
      </>
    ),
  };
};

export const HAS_DATA_PRIVACY_OFFICER_SLUG = "add-dpo-question";
const hasDataPrivacyOfficerQuestion = (
  answers: Record<string, string>,
  privacyCenter: PrivacyCenterDto,
  pcSettings: PrivacyCenterSettingsProps,
): SurveyQuestionProps => {
  let isDpoRequired = isDataPrivacyOfficerRequired(answers, privacyCenter);
  let hasDpo = hasDataPrivacyOfficer(pcSettings);

  return {
    type: "boolean",
    slug: "add-dpo-question",
    visible: true,
    question: isDpoRequired
      ? "A Data Privacy Officer is required. Do you want to add your DPO details now?"
      : "Does your business have a Data Privacy Officer?",
    helpText: isDpoRequired ? (
      <div>
        <p>
          A Data Privacy Officer (DPO) monitors your organization's compliance activities and serves
          as a point of contact for both consumers and the data protection authorities in Europe and
          the UK. Your business requires a DPO because you answered above that it regularly and
          systematically monitors individuals in Europe or the UK on a large scale.{" "}
          <a
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
            href="https://knowledge.truevault.com/article/224-data-protection-officers"
          >
            You can read more about data privacy officers here.
          </a>
        </p>
        <p>
          If you have a DPO, add their details below and we will include this in your GDPR Notice.
          If you do not currently have a DPO but will get one, select “I'll do this later”; we'll
          send you a reminder to complete this item on your to-do list. If you would like help
          finding a DPO, email{" "}
          <a href="mailto:help@truevault.com" className="text-blue-600 underline">
            help@truevault.com
          </a>
          .
        </p>
      </div>
    ) : (
      <div>
        <p>
          Based on your answers above, your business does not require a Data Privacy Officer (DPO)
          because it does not process data on a "large scale." However, if you have a DPO anyway,
          you can add their details below and we will include it in your GDPR Notice.
        </p>
        <p>
          A DPO monitors your organization's compliance activities and serves as a point of contact
          for both consumers and the data protection authorities in Europe and the UK.
        </p>
      </div>
    ),
    yesLabel: isDpoRequired ? "Set DPO" : "Yes - Add DPO",
    noLabel: isDpoRequired ? "Do This Later" : "No",
    noDisabled: hasDpo, // if DPO is already set, then user can only modify existing DPO (but not remove it)
    noDisabledTooltip: "You have already added a DPO.",
  };
};

export const HAS_DESIGNATED_REP_SLUG = "has-designated-representative";
const hasDesignatedRepQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: HAS_DESIGNATED_REP_SLUG,
    question:
      'Your business may be required to have a "Designated Representative" in the EEA/UK. Do you already have a Designated Representative?',
    helpText: (
      <div>
        <p>
          You are required to have a Data Representative in the EEA/UK because you provide products
          or services there but answered that you do not have a physical presence there. Read more
          about{" "}
          <a
            target="_blank"
            className="text-blue-600 underline"
            rel="noreferrer"
            href="https://knowledge.truevault.com/article/316-designating-a-representative-in-the-eea-uk"
          >
            Designated Representatives.
          </a>{" "}
        </p>
      </div>
    ),
    yesLabel: "Yes",
    noLabel: "No",
  };
};

export const AUTOMATED_DECISION_MAKING_SLUG = "automated-decision-making";
export const automatedDecisionMakingQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: AUTOMATED_DECISION_MAKING_SLUG,
    question:
      "Does your business engage in automated decision making (including individual profiling) which produces legal or similarly significant effects for EEA/UK/CH residents?",
    helpText:
      "“Similarly significant effects” include automated decisions by your business to provide or deny a person money or credit, housing, insurance, education enrollment, criminal justice, employment opportunities, health care services, or access to basic necessities, such as food and water.",
  };
};

export const OUTBOUND_SALES_SLUG = "outbound-sales-eea-uk";
export const outboundSalesQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: OUTBOUND_SALES_SLUG,
    question:
      "Do you send outbound sales emails or phone calls to people in the EEA/UK/CH that you don’t have a preexisting relationship with?",
    helpText:
      "Answer “Yes” if you send emails (or make telephone calls) to people who are not existing customers, or who have not provided their contact information directly to you for the purpose of sales/marketing.",
  };
};

export const EXTRA_LAWFUL_BASES_PUBLIC_INTEREST_SLUG = "extra-lawful-bases-public-interest";
export const extraLawfulBasesPublicInterestQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: EXTRA_LAWFUL_BASES_PUBLIC_INTEREST_SLUG,
    question:
      "It's very unlikely, but does your business process personal data that is necessary for the performance of a task carried out in the public interest or the exercise of official authority?",
    helpText: "For example, a utility company providing services to the public.",
  };
};

export const EXTRA_LAWFUL_BASES_VITAL_INTEREST_SLUG = "extra-lawful-bases-vital-interest";
export const extraLawfulBasesVitalInterestQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: EXTRA_LAWFUL_BASES_VITAL_INTEREST_SLUG,
    question:
      "It's very unlikely, but does your business process personal data that is necessary in order to protect information essential for someone's life, or in a situation involving public health or safety?",
  };
};

const eeaVisibility = (answers: Record<string, string>): Record<string, boolean> => {
  return {
    [PERSONAL_DATA_LARGE_SCALE_QUESTION_SLUG]: true,
    [EEA_PHYSICAL_PRESENCE_SLUG]: true,
    [HAS_DATA_PRIVACY_OFFICER_SLUG]: true,
    [HAS_DESIGNATED_REP_SLUG]: answers[EEA_PHYSICAL_PRESENCE_SLUG] === "false",
    [AUTOMATED_DECISION_MAKING_SLUG]: true,
    [OUTBOUND_SALES_SLUG]: true,
    [EXTRA_LAWFUL_BASES_PUBLIC_INTEREST_SLUG]: true,
    [EXTRA_LAWFUL_BASES_VITAL_INTEREST_SLUG]: true,
  };
};
const eeaQuestions = (
  answers: Record<string, string>,
  privacyCenter: PrivacyCenterDto,
  pcSettings: PrivacyCenterSettingsProps,
): SurveyQuestionProps[] => {
  return [
    largeScaleQuestion(), // personal-data-large-scale
    eeaPhysicalPresenceQuestion(), // eea-physical-presence
    hasDataPrivacyOfficerQuestion(answers, privacyCenter, pcSettings), // add-dpo-question
    hasDesignatedRepQuestion(), // has-designated-representative
    automatedDecisionMakingQuestion(), // automated-decision-making
    outboundSalesQuestion(), // outbound-sales-eea-uk
    extraLawfulBasesPublicInterestQuestion(), // extra-lawful-bases-public-interest
    extraLawfulBasesVitalInterestQuestion(), // extra-lawful-bases-vital-interest
  ];
};

export const eeaUkSurvey = (
  answers: Record<string, string>,
  privacyCenter: PrivacyCenterDto,
  pcSettings: PrivacyCenterSettingsProps,
): SurveyDefinition => {
  return {
    questions: eeaQuestions(answers, privacyCenter, pcSettings),
    visibility: eeaVisibility(answers),
    disabled: {},
  };
};

// Note that this should only be called if the org has GDPR enabled (since DPO doesn't apply to non-GDPR orgs)
export function isDataPrivacyOfficerRequired(
  eeaUkAnswers: Record<string, string>,
  privacyCenter?: PrivacyCenterDto,
) {
  return (
    eeaUkAnswers[PERSONAL_DATA_LARGE_SCALE_QUESTION_SLUG] === "true" || privacyCenter?.requiresDpo
  );
}

export function hasDataPrivacyOfficer(pcSettings: PrivacyCenterSettingsProps) {
  return pcSettings && Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail);
}

export function hasDesignatedRepresentative(pcSettings: PrivacyCenterSettingsProps) {
  return (
    pcSettings?.eeaContactName?.length > 0 &&
    pcSettings?.eeaContactEmail?.length > 0 &&
    pcSettings?.eeaContactAddress?.length > 0
  );
}
